import { ReactNode, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { N700 } from "@atlaskit/theme/colors";

import { ProductKey } from "@/components/signup/utils";

import { useSignupFormContext } from "../../../../signup-form/hooks";
import { ReturnToExistingProduct } from "../../return-to-existing-product";
import { DefaultSiteName } from "../default-site-name";
type ContactExpandType = {
    productKeyForExpand: string;
};
export const ContactExpand = ({ productKeyForExpand }: ContactExpandType) => {
    const createNewWorkspace = useMemo(() => {
        if (typeof window === "undefined") return false;
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get("createNew") === "true";
    }, []);

    const [showSiteNaming, setShowSiteNaming] = useState(createNewWorkspace);
    const { productName } = useSignupFormContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const clickReturnToExistingWorkspace = () => {
        setShowSiteNaming(false);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "contactExpandGoToReturnToProductScreen",
            attributes: {
                productName
            }
        }).fire();
    };

    return (
        <div css={containerCss}>
            {showSiteNaming ? (
                <>
                    <DefaultSiteName />
                    {productKeyForExpand === ProductKey.CONFLUENCE ? (
                        <div
                            css={[
                                returnToContactExpandLink,
                                returnToExistingWorkspace
                            ]}
                        >
                            <FormattedMessage
                                id="BxpSignup.SiteExpansionForm.goToExistingWorkspace"
                                defaultMessage="Or<link>visit an existing workspace</link>"
                                values={{
                                    link: (str: ReactNode) => (
                                        <Button
                                            appearance="link"
                                            onClick={
                                                clickReturnToExistingWorkspace
                                            }
                                        >
                                            {str}
                                        </Button>
                                    )
                                }}
                            />
                        </div>
                    ) : (
                        <div css={returnToContactExpandLink}>
                            <Button
                                appearance="link"
                                onClick={clickReturnToExistingWorkspace}
                            >
                                <FormattedMessage
                                    id="bxpSignup.companyExpand.returnToJoinSites"
                                    defaultMessage="Join an existing {productName} site"
                                    values={{ productName }}
                                />
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <ReturnToExistingProduct
                    productKeyForExpand={productKeyForExpand}
                    exitView={() => {
                        setShowSiteNaming(true);
                        createAnalyticsEvent({
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "button",
                            actionSubjectId:
                                "contactExpandGoToSiteNamingScreen",
                            attributes: {
                                productName
                            }
                        }).fire();
                    }}
                />
            )}
        </div>
    );
};

const containerCss = css({
    alignItems: "center",
    color: N700,
    display: "flex",
    flexDirection: "column"
});

const returnToContactExpandLink = css({
    marginBlockStart: "42px"
});

const returnToExistingWorkspace = css({
    alignSelf: "flex-start",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        marginBlockStart: "-3px",
        marginInlineStart: "-8px"
    }
});
